
import {
  alertController,
  IonButton,
  IonCard,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonNote,
  IonPage,
  IonSelect,
  IonSelectOption,
  IonTitle,
  IonToolbar,
  IonRadio,
  IonRadioGroup,
  modalController
} from "@ionic/vue";
import { arrowUndoOutline } from "ionicons/icons";
import { defineComponent } from 'vue';
import { mapGetters, useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { DateTime } from 'luxon';
import { Redirect } from "@shopify/app-bridge/actions";
import createApp from "@shopify/app-bridge";
import PickupLocationModal from "./PickupLocationModal.vue";
import { translate } from "@/i18n";
import { showToast } from "@/utils";

export default defineComponent({
  name: 'OrderDetail',
  components: {
    IonButton,
    IonCard,
    IonContent,
    IonHeader,
    IonItem,
    IonIcon,
    IonLabel,
    IonList,
    IonNote,
    IonPage,
    IonSelect,
    IonSelectOption,
    IonTitle,
    IonToolbar,
    IonRadio,
    IonRadioGroup
  },
  data() {
    return {
      initialOrder: {} as any,
      deliveryMethods: [
        {
          name: 'Store pickup',
          value: 'STOREPICKUP'
        },
        {
          name: 'Shipping',
          value: 'STANDARD'
        }
      ]
    }
  },
  computed: {
    ...mapGetters({
      order: 'order/getDraftOrder',
      shopifyStores: 'shop/getStores',
      getPreorderItemAvailability: 'stock/getPreorderItemAvailability',
      routeParams: 'shop/getRouteParams'
    })
  },

  async mounted(){
    if (this.$route.query.id) {
      await this.store.dispatch('order/getDraftOrder', this.$route.query.id);
    }
    this.initialOrder = JSON.parse(JSON.stringify(this.order));
  },
  methods: {
    isPreorderOrBackorderProduct(item: any, label: string){
      const product = this.getPreorderItemAvailability(item.sku);  
      return !(product.label === label);
    },
    updateDeliveryMethod(event: any, item: any) {
      item.deliveryMethodTypeId = event.detail.value;
      if (item.deliveryMethodTypeId !== 'STOREPICKUP') item.properties = item.properties.filter((property: any) => !(property.name === '_pickupstore' || property.name === 'Store Pickup' || property.name === 'Pickup Store'))
      this.store.dispatch('order/updateLineItems', this.order)
    },
    markPreorderBackorderItem (item: any, event: any) {
      const product = this.getPreorderItemAvailability(item.sku)
      item.properties.push({ name: 'Note', value: event.detail.value });
      if(product.estimatedDeliveryDate){
        item.properties.push({ name: 'PROMISE_DATE', value: DateTime.fromISO(product.estimatedDeliveryDate).toFormat("MM/dd/yyyy") })
      }
      this.store.dispatch('order/updateLineItems', this.order);
    },
    async updateDraftOrder () {
      const shopConfig = JSON.parse(process.env.VUE_APP_SHOPIFY_SHOP_CONFIG);
      this.order.line_items.map((lineItem: any) => delete lineItem.deliveryMethodTypeId)
      await this.store.dispatch('order/updateDraftOrder', this.order).then(() => {
        const app = createApp({
          apiKey: shopConfig[this.routeParams.shop].apiKey,
          host: this.routeParams.host
        });
        Redirect.create(app).dispatch(Redirect.Action.ADMIN_PATH, `/draft_orders/${this.routeParams.id}`);
      })
    },
    checkPreorderBackorderItem (item: any) {
      const property = item.properties?.find((property: any) => property.name === 'Note')?.value;
      return property ? property : "" ;
    },
    timeFromNow (time: string) {
      if (time) {
        const timeDiff = DateTime.fromISO(time).diff(DateTime.local());
        return DateTime.local().plus(timeDiff).toRelative();
      }
    },
    getEstimatedDeliveryDate(item: any, label: string){
      const labelMapping = {
        "BACKORDER": "Back Order",
        "PRE-ORDER": "Pre Order",
      } as any
      if(this.checkPreorderBackorderItem(item) === labelMapping[label]) {
        return item.properties.find((property: any) => property.name === "PROMISE_DATE") ? item.properties.find((property: any) => property.name === "PROMISE_DATE").value : "";
      }
      const product = this.getPreorderItemAvailability(item.sku);
      if(product.label === label){
        return DateTime.fromISO(product.estimatedDeliveryDate).toFormat("MM/dd/yyyy");
      }
    },
    async updatePickupLocation(item: any) {
      const modal = await modalController
        .create({
          component: PickupLocationModal,
          // Adding backdropDismiss as false because on dismissing the modal through backdrop,
          // backrop.role returns 'backdrop' giving unexpected result
          backdropDismiss: false,
          componentProps: { item, facilityId: item.properties.find((property: any) => property.name == '_pickupstore')?.value }
        })
      modal.onDidDismiss().then((result) => {
        if (result.role) {
          // role will have the passed data
          const facilityData = result.role as any
          item.selectedFacility = facilityData.selectedFacility
          item.properties.push({ name: '_pickupstore', value: facilityData.storeCode }, { name: 'Store Pickup', value: item.selectedFacility })
          this.store.dispatch('order/updateLineItems', this.order);
        }
      });
      return modal.present();
    },
    async save() {
      const message = this.$t("Are you sure you want to save the changes?");
      const alert = await alertController.create({
        header: this.$t("Save changes"),
        message,
        buttons: [
          {
            text: this.$t("Cancel"),
          },
          {
            text: this.$t("Confirm"),
            handler: () => {
              this.updateDraftOrder()
            }
          }
        ],
      });
      return alert.present();
    },
    isChanged() {
      if (Object.keys(this.order).length && Object.keys(this.initialOrder).length) {
        return this.order.line_items.some((updatedItem: any, index: number) => {
          const isMethodUpdated = updatedItem.deliveryMethodTypeId !== this.initialOrder.line_items[index].deliveryMethodTypeId
          return updatedItem.deliveryMethodTypeId !== 'STOREPICKUP' ? isMethodUpdated : isMethodUpdated && updatedItem.selectedFacility;
        })
      }
    },
    async undo(item: any) {
      const header = this.$t('Clear edits')
      const message = this.$t('Are you sure you want to undo the changes you’ve made to this order item?')

      const alert = await alertController
        .create({
          header: header,
          message: message,
          buttons: [{
            text: this.$t('Cancel'),
            role: 'cancel'
          },{
            text: this.$t('Clear'),
            handler: async () => {
              // finding the initial line_item (without edits) and updating it in this.order
              const initialItem = this.initialOrder.line_items.find((lineItem: any) => lineItem.id === item.id)
              const index = this.order.line_items.findIndex((lineItem: any) => lineItem.id === item.id)
              this.order.line_items.splice(index, 1, initialItem)
              this.store.dispatch('order/updateLineItems', this.order)
              showToast(translate('Previous edits cleared successfully'))
            }
          }]
        });
      return alert.present();
    }
  },
  setup() {
    const store = useStore();
    const router = useRouter();

    return {
      arrowUndoOutline,
      router,
      store
    };
  },
});
